import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CentrifugeProgressMessage } from '@/hooks/useCentrifugeProgress';
import {
  useLazyGetProjectJobQuery,
  useLazyListProjectJobsQuery,
} from '../api/indexerApi';
import type { ProjectJob, ProjectJobKind } from '../api/types';
import { isJobDone } from './job-labels';
import {
  mergeJobFromFetch,
  mergeJobListItemFromFetch,
  mergeJobLiveProgress,
  mergeJobsListFromFetch,
} from './job-live-result';
import { parseUtcDate, toUtcIso } from './datetime';
import { HISTORY_WINDOW_DAYS, startOfRange } from './job-range';

function sortJobsNewestFirst(jobs: ProjectJob[]): ProjectJob[] {
  return [...jobs].sort(
    (a, b) =>
      parseUtcDate(b.createdAt).getTime() - parseUtcDate(a.createdAt).getTime(),
  );
}

export function useJobHistory(
  projectId: string | undefined,
  kind: ProjectJobKind | ProjectJobKind[],
  jobQueryKey = 'job',
) {
  const kinds = Array.isArray(kind) ? kind : [kind];
  const kindsKey = kinds.join(',');
  const [searchParams, setSearchParams] = useSearchParams();
  const jobParam = searchParams.get(jobQueryKey);
  const [jobs, setJobs] = useState<ProjectJob[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(jobParam);
  const [selectedJob, setSelectedJob] = useState<ProjectJob | null>(null);
  const [loadingList, setLoadingList] = useState(Boolean(projectId));
  const [loadingJob, setLoadingJob] = useState(false);

  const [listJobs] = useLazyListProjectJobsQuery();
  const [getJob] = useLazyGetProjectJobQuery();

  const refresh = useCallback(async () => {
    if (!projectId) {
      setJobs([]);
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    try {
      const since = toUtcIso(startOfRange(HISTORY_WINDOW_DAYS))!;
      const listed = await listJobs(
        {
          projectId,
          kind: kinds.length === 1 ? kinds[0] : kinds,
          since,
        },
        false,
      ).unwrap();
      setJobs((prev) =>
        mergeJobsListFromFetch(prev, sortJobsNewestFirst(listed)),
      );
    } finally {
      setLoadingList(false);
    }
  }, [projectId, kindsKey, listJobs]);

  useEffect(() => {
    setSelectedId(jobParam);
    if (!jobParam) setSelectedJob(null);
  }, [jobParam]);

  useEffect(() => {
    if (!projectId) {
      setJobs([]);
      setLoadingList(false);
      return;
    }
    void refresh().catch(() => setJobs([]));
  }, [projectId, kindsKey, refresh]);

  useEffect(() => {
    if (!projectId || !selectedId) {
      setSelectedJob(null);
      return;
    }

    let cancelled = false;
    let interval: number | undefined;

    const load = async (withSpinner: boolean) => {
      if (withSpinner) setLoadingJob(true);
      try {
        // همیشه از شبکه بخوان — preferCacheValue:true کش خالی اول جاب را
        // برمی‌گرداند و ردیف‌های سوکت را پاک می‌کند.
        const next = await getJob(
          { projectId, jobId: selectedId },
          false,
        ).unwrap();
        if (cancelled) return next;
        setSelectedJob((prev) => mergeJobFromFetch(prev, next));
        return next;
      } catch {
        // خطای موقت GET نباید ردیف‌های زندهٔ سوکت را پاک کند
        return null;
      } finally {
        if (withSpinner && !cancelled) setLoadingJob(false);
      }
    };

    void (async () => {
      const first = await load(true);
      if (cancelled || !first || isJobDone(first.state)) {
        if (first && isJobDone(first.state)) void refresh().catch(() => undefined);
        return;
      }
      // Inspect / Google Index progress comes from Centrifugo; slow poll as fallback.
      const pollMs =
        first.kind === 'inspect' || first.kind === 'google_index'
          ? 15_000
          : 2_000;
      interval = window.setInterval(() => {
        void (async () => {
          const next = await load(false);
          if (next && isJobDone(next.state)) {
            if (interval) window.clearInterval(interval);
            interval = undefined;
            void refresh().catch(() => undefined);
          }
        })();
      }, pollMs);
    })();

    return () => {
      cancelled = true;
      if (interval) window.clearInterval(interval);
    };
  }, [projectId, selectedId, refresh, getJob]);

  function select(jobId: string) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(jobQueryKey, jobId);
        return next;
      },
      { replace: true },
    );
  }

  function close() {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete(jobQueryKey);
        return next;
      },
      { replace: true },
    );
  }

  async function openJob(jobId: string) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(jobQueryKey, jobId);
        return next;
      },
      { replace: true },
    );
    await refresh().catch(() => undefined);
  }

  const applyLiveProgress = useCallback(
    (data: CentrifugeProgressMessage) => {
      if (
        !selectedId ||
        (!data.latestResult &&
          data.successCount == null &&
          data.failedCount == null &&
          !data.state)
      ) {
        return;
      }

      setSelectedJob((prev) => {
        if (!prev || prev.id !== selectedId) return prev;
        return mergeJobLiveProgress(prev, data);
      });

      if (
        data.successCount != null ||
        data.failedCount != null ||
        data.state
      ) {
        setJobs((prev) =>
          prev.map((job) =>
            job.id !== selectedId
              ? job
              : mergeJobLiveProgress(job, data),
          ),
        );
      }
    },
    [selectedId],
  );

  const refreshSelected = useCallback(async () => {
    if (!projectId || !selectedId) return;
    try {
      const next = await getJob(
        { projectId, jobId: selectedId },
        false,
      ).unwrap();
      setSelectedJob((prev) => mergeJobFromFetch(prev, next));
      setJobs((prev) =>
        prev.map((job) =>
          job.id !== selectedId ? job : mergeJobListItemFromFetch(job, next),
        ),
      );
    } catch {
      /* keep live socket rows if refresh fails after finish */
    }
  }, [projectId, selectedId, getJob]);

  return {
    jobs,
    selectedId,
    selectedJob,
    loadingList,
    loadingJob,
    select,
    close,
    refresh,
    refreshSelected,
    applyLiveProgress,
    openJob,
  };
}
